import { useNavigate } from 'react-router';
import { useNotifications, useTimeZone } from '@connect-x-app/hybrid-state';
import { Text } from '@connect-x-app/hybrid-ui';
import {
  Notification,
  NotificationChannelType,
  NotificationLink,
  NotificationStatus,
  NotificationType,
} from '@connect-x-app/shared-domain-models';
import { COLORS } from '@connect-x-app/shared-ui';
import { NOTIFICATION_TEST_IDS } from '@connect-x-app/web-test-ids';
import { Icon } from '@connect-x-app/web-ui';
import classNames from 'classnames';

import './NotificationItem.scss';

type NotificationItemProps = {
  notification: Notification;
  onClick: () => void;
};

const buildNotificationPath = ({ channelId, channelType, messageId, roomId }: NotificationLink): string => {
  const queryString = messageId ? `?externalMessageId=${messageId}` : '';

  if (channelType === NotificationChannelType.CUSTOM) {
    return `/dashboard/flightrooms/${roomId}/channel/${channelId}${queryString}`;
  }

  if (channelId) {
    return `/dashboard/flightrooms/${roomId}/${channelId}${queryString}`;
  }

  return `/dashboard/flightrooms/${roomId}${queryString}`;
};

export const NotificationItem = ({ notification, onClick }: NotificationItemProps) => {
  const { formatDateInTimeZone } = useTimeZone();
  const { dismissNotification } = useNotifications();
  const navigate = useNavigate();

  const isDismissed = notification.status === NotificationStatus.DISMISSED;

  const onPressNotification = () => {
    dismissNotification(notification.id);

    if (notification.type === NotificationType.ACCESS_DENIED) {
      onClick();
      return;
    }

    navigate(buildNotificationPath(notification.link));
    onClick();
  };

  return (
    <div className={classNames('notification-center-item', !isDismissed && 'unread')} onClick={onPressNotification}>
      <div className="notification-item-icon">
        {notification.type === NotificationType.MENTION ? (
          <MentionIcon outlined={isDismissed} />
        ) : (
          <UnreadDot hide={isDismissed} />
        )}
      </div>
      <div className="notification-item-text">
        <Text size="SM" weight={isDismissed ? 'regular' : 'bold'}>
          {notification.title}
        </Text>
        <Text size="SM" weight={isDismissed ? 'regular' : 'bold'}>
          {notification.body}
        </Text>
        <Text size="XS">{formatDateInTimeZone(notification.createdAt) ?? ''}</Text>
      </div>
    </div>
  );
};

type UnreadDotProps = {
  hide: boolean;
};

const UnreadDot = ({ hide }: UnreadDotProps) => {
  return <div className={classNames('unread-dot', hide ? 'hide' : null)} data-test={NOTIFICATION_TEST_IDS.item.unreadDot} />;
};

type MentionIconProps = {
  outlined: boolean;
};

const MentionIcon = ({ outlined }: MentionIconProps) => {
  return (
    <Icon
      className="mention-icon"
      colorName={COLORS.romeo.$07}
      name={outlined ? 'mentionOutline' : 'mentionFilled'}
      testId={NOTIFICATION_TEST_IDS.item.mentionIcon}
    />
  );
};
